import { AsyncButton, loadWrapper } from "aic";
import { useAppSettings, useHierarchyContext, useTlfContext } from "contexts/AppSettingsContext";
import { eCommentReviewStatus } from "functions/api/comments/interface";
import { PatchCommentDetails, patchCommentStatus } from "functions/api/comments/patchCommentStatus";
import { eAccessRights } from "functions/api/enums";
import { IRequestStatus } from "functions/api/interface";
import { MouseEventHandler, ReactNode, useCallback, useMemo, useState } from "react";
import { CommentViewContext } from "./interface";
import { useComments } from "./useComments";

interface ResolveCommentButtonProps {
  activeContext: CommentViewContext;
  commentId: string;
  disabled?: boolean;
}
/**
 * Toggle resolve/open status for a single comment.
 *
 * @param props - Component props.
 * @param props.activeContext - Active comment view context.
 * @param props.commentId - Id of the comment.
 * @param props.disabled - When true, button is disabled.
 */
export const ResolveCommentButton = ({
  activeContext,
  commentId,
  disabled,
}: ResolveCommentButtonProps): ReactNode => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const hierarchy = hierarchyContext?.hierarchy;
  const tlfContext = useTlfContext();
  const tlfData = tlfContext.tlfData;

  const { comments, readComment, setComments } = useComments(activeContext);
  const thisComment = readComment(commentId);
  const newStatus =
    thisComment?.commentReviewStatus === eCommentReviewStatus.resolved
      ? eCommentReviewStatus.open
      : eCommentReviewStatus.resolved;
  const buttonAction = newStatus === eCommentReviewStatus.resolved ? "Resolve" : "Open";

  const thisCommentRequestDto: PatchCommentDetails | null = useMemo(() => {
    if (activeContext === "Hierarchy" && hierarchy) {
      return {
        activeContext: "Hierarchy",
        hierarchyId: hierarchy.id,
        commentId,
        newStatus,
      };
    } else if (["TlfItem"].includes(activeContext) && tlfData) {
      return {
        activeContext: "TlfItem",
        id: tlfData.id,
        azsolId: tlfData.azsolId,
        version: tlfData.version,
        commentId,
        newStatus,
      };
    }
    return null;
  }, [activeContext, commentId, hierarchy, newStatus, tlfData]);

  const [patchCommentStatusRequest, setPatchCommentStatusRequest] = useState<
    IRequestStatus<PatchCommentDetails>
  >({
    requesting: false,
    error: false,
  });

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      /* istanbul ignore else */
      if (thisCommentRequestDto) {
        const request = await loadWrapper(
          patchCommentStatus,
          thisCommentRequestDto,
          { ...patchCommentStatusRequest, requestedId: undefined, error: false },
          setPatchCommentStatusRequest,
          appSettings.backEndUrl,
        );
        /* istanbul ignore else */
        if (request?.success) {
          const newComments = [...comments.filter((c) => c.id !== request.data.id), request.data];
          setComments(newComments, true);
        }
      }
    },
    [
      appSettings.backEndUrl,
      comments,
      patchCommentStatusRequest,
      setComments,
      thisCommentRequestDto,
    ],
  );

  return (
    thisComment && (
      <AsyncButton
        id={`resolve-comment-${commentId}`}
        aria-label={`${buttonAction} comment`}
        className="resolve-button"
        title={`${buttonAction} this comment`}
        size="sm"
        variant={newStatus === eCommentReviewStatus.resolved ? "success" : "warning"}
        disabled={
          disabled ||
          !hierarchyContext?.accessRights.includes(eAccessRights.MaintainHierarchyComments) ||
          !thisCommentRequestDto
        }
        requestStatus={patchCommentStatusRequest}
        onClick={handleClick}
      >
        {buttonAction}
      </AsyncButton>
    )
  );
};

ResolveCommentButton.displayName = "ResolveCommentButton";
